import React, { useEffect, useState } from "react";
import { useAppDispatch } from "store";
import { fetchRequestableParts } from "store/slices/dialogs/addPart.store";
import { StockStore, PartType } from "operations/schema/schema";
import { Grid, Autocomplete } from "@mui/material";
import StyledTextField from "components/StyledTextField";
import SecondaryButton from "components/SecondaryButton";
import { FormattedMessage, useIntl } from "react-intl";

export interface PartSelection {
  id?: string;
  name: string;
  quantity: number;
  price: number;
  isNonStock: boolean;
  nominalCode: string;
  costCentreCode: string;
  departmentCode: string;
}

interface PartsSelectorProps {
  open: boolean;
  parts: PartSelection[];
  setParts: (parts: PartSelection[]) => void;
}

const PartsSelector: React.FC<PartsSelectorProps> = ({ open, parts, setParts }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const [partList, setPartList] = useState<PartType[]>([]);

  useEffect(() => {
    if (open) {
      const partQueryInput = { locationId: "", stockStore: StockStore.Engineer };
      dispatch(fetchRequestableParts([partQueryInput]))
        .then((res: any) => {
          if (res?.payload) {
            setPartList(res.payload);
          }
        })
        .catch((error: any) => {
          console.error("Error fetching parts:", error);
        });
    }
  }, [open, dispatch]);

  const updatePart = (index: number, update: Partial<PartSelection>) => {
    const updated = [...parts];
    updated[index] = { ...updated[index], ...update };
    setParts(updated);
  };

  const removePart = (index: number) => {
    const updated = [...parts];
    updated.splice(index, 1);
    setParts(updated);
  };

  const addPart = () => {
    setParts([...parts, { id: "", name: "", quantity: 1, price: 1, isNonStock: false, nominalCode: "", costCentreCode: "", departmentCode: "" }]);
  };

  return (
    <Grid item>
      {parts.map((part, index) => (
        <Grid container spacing={1} alignItems="center" key={index} sx={{ mb: 1 }}>
          <Grid item xs={7}>
            <Autocomplete
                fullWidth
                data-testid="AddPart-SelectPart"
                options={partList}
                loading={false}
                getOptionLabel={(option: PartType) => option.description || option.partNumber || ""}
                value={partList.find((p) => p.id === part.id) || null}
                onChange={(event, newValue) => {
                    updatePart(index, {
                    id: newValue?.id || "",
                    name: newValue?.description || "",
                    price: newValue?.baseSoftPrice ?? 0,
                    isNonStock: newValue?.isNonStock ?? false,
                    nominalCode: newValue?.nominalCode ?? "",
                    costCentreCode: newValue?.costCentreCode ?? "",
                    departmentCode: newValue?.departmentCode ?? ""
                    });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                ListboxProps={{
                    style: {
                    maxHeight: 200, 
                    overflowY: 'auto',
                    },
                }}
                componentsProps={{
                    popper: {
                    modifiers: [
                        { name: 'flip', enabled: false },
                        { name: 'preventOverflow', options: { boundary: 'viewport' } },
                    ],
                    },
                }}
                renderInput={(params) => (
                    <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "part", defaultMessage: "Part" })}
                    required
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                    />
                )}
                />
          </Grid>
          <Grid item xs={3}>
            <StyledTextField
              type="number"
              fullWidth
              label={intl.formatMessage({
                id: "createJob.quantity",
                defaultMessage: "Qty",
              })}
              value={part.quantity}
              onChange={(e) => {
                updatePart(index, { quantity: parseInt(e.target.value, 10) || 0 });
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <SecondaryButton onClick={() => removePart(index)}>
              <FormattedMessage id="general.remove" defaultMessage="Remove" />
            </SecondaryButton>
          </Grid>
        </Grid>
      ))}

      <SecondaryButton onClick={addPart} sx={{ mt: 1 }}>
        <FormattedMessage id="createJob.addPart" defaultMessage="Add Part" />
      </SecondaryButton>
    </Grid>
  );
};

export default PartsSelector;